import { useCallback, useMemo } from 'react'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import DropdownCheckbox from 'components/UI/DropdownCheckbox/DropdownCheckbox'
import FreeTextMultiSelect, { DisplayChip } from 'components/UI/FreeTextMultiSelect/FreeTextMultiSelect'


type DistributionList = {
  id: string
  name: string
  recipients: string[]
}

const normalizeEmail = (s: string) => s.trim().toLowerCase()
const dedupeEmails = (emails: string[]) => Array.from(new Set((emails ?? []).map(normalizeEmail))).filter(Boolean)

const buildListById = (lists: DistributionList[]) => {
  const map = new Map<string, DistributionList>()
  for (const l of lists ?? []) map.set(l.id, l)
  return map
}

const buildDisplayChips = (
  fullListById: Map<string, DistributionList>,
  selected: DistributionList[],
  onDeleteGroupChip: (deletedEmail: string, listId: string) => void
): DisplayChip[] => {
  const chips: DisplayChip[] = []

  for (const sel of selected ?? []) {
    const full = fullListById.get(sel.id) ?? sel
    const listRecipients = full.recipients ?? []

    for (const raw of listRecipients) {
      const email = normalizeEmail(raw)
      if (!email) continue

      chips.push({
        value: email,
        key: `${full.id}:${email}`,
        tooltip: `Member of ${full.name} group`,
        chipProps: { color: 'success' },
        onDelete: () => onDeleteGroupChip(email, full.id),
      })
    }
  }

  return chips
}

export interface RecipientFieldsProps {
  distributionLists: DistributionList[]
  selectedDistributionLists: DistributionList[]
  setSelectedDistributionLists: (lists: DistributionList[]) => void
  recipients: string[]
  setRecipients: (emails: string[]) => void
  required?: boolean
  error?: boolean
  onFocus?: () => void
  sx?: SxProps
}

export default function RecipientFields({
  distributionLists,
  selectedDistributionLists,
  setSelectedDistributionLists,
  recipients,
  setRecipients,
  required = false,
  error = false,
  onFocus = () => {},
  sx = {},
}: RecipientFieldsProps) {
  const listById = useMemo(
    () => buildListById(distributionLists ?? []),
    [distributionLists]
  )

  const handleDeleteGroupChip = useCallback((deletedEmail: string, listId: string) => {
    const normalizedDeleted = normalizeEmail(deletedEmail)

    const list = distributionLists.find(l => l.id === listId)
    if (!list) return

    // keep the order exactly as defined in the list
    const remaining = (list.recipients ?? [])
      .map(normalizeEmail)
      .filter(email => email && email !== normalizedDeleted)

    // prepend to existing blue chips
    setRecipients(
      dedupeEmails([
        ...remaining,
        ...(recipients ?? []),
      ])
    )

    // uncheck the group
    setSelectedDistributionLists(
      (selectedDistributionLists ?? []).filter(l => l.id !== listId)
    )
  }, [
    distributionLists,
    recipients,
    selectedDistributionLists,
    setRecipients,
    setSelectedDistributionLists,
  ])

  // green chips are derived from selected lists + full collection lookup
  const displayChips = useMemo(
    () => buildDisplayChips(listById, selectedDistributionLists ?? [], handleDeleteGroupChip),
    [listById, selectedDistributionLists, handleDeleteGroupChip]
  )

  // value is only blue chips (ad-hoc recipients)
  const blueRecipients = useMemo(
    () => dedupeEmails(recipients ?? []),
    [recipients]
  )

  return (
    <Stack direction='column' gap={2} sx={sx}>
      <DropdownCheckbox<DistributionList>
        label='Group Lists'
        collection={distributionLists ?? []}
        selected={selectedDistributionLists ?? []}
        setSelected={(selected) => setSelectedDistributionLists(selected ?? [])}
        renderItem={(item) => item.name}
        required={required}
        error={error}
        onFocus={onFocus}
      />

      <FreeTextMultiSelect
        label='Email(s)'
        value={blueRecipients}
        setValue={(next) => setRecipients(dedupeEmails(next))}
        required={required}
        error={error}
        onFocus={onFocus}
        sx={{ width: '100%' }}
        displayChips={displayChips}
      />
    </Stack>
  )
}
